import { CalendarEvent } from '../types';
import { WorkingHours, DayOfWeek } from '../components/settings/BookingSettings';
import { parse, addMinutes, isBefore, isAfter, isEqual, format, isSameDay } from 'date-fns';
import { RRule } from 'rrule';

export function expandRecurringEvents(
    events: CalendarEvent[],
    windowStart: Date,
    windowEnd: Date
): CalendarEvent[] {
    const expanded: CalendarEvent[] = [];

    events.forEach(event => {
        if (!event.is_recurring || !event.recurrence_rule) {
            expanded.push(event);
            return;
        }

        try {
            const originalStart = new Date(event.start_time);
            const originalEnd = new Date(event.end_time);
            const durationMs = originalEnd.getTime() - originalStart.getTime();

            const options = RRule.parseString(event.recurrence_rule);
            // rrule exige dtstart para calcular as ocorrências a partir dele
            options.dtstart = originalStart;
            
            const rule = new RRule(options);
            // Pega todas as ocorrências na janela atual
            const occurrences = rule.between(windowStart, windowEnd, true);

            occurrences.forEach(date => {
                // date retornado pelo rrule é a data de início da ocorrência
                const occStart = new Date(date);
                const occEnd = new Date(occStart.getTime() + durationMs);

                expanded.push({
                    ...event,
                    id: `${event.id}_${occStart.getTime()}`, // Cria um ID único para a instância
                    start_time: occStart.toISOString(),
                    end_time: occEnd.toISOString()
                });
            });
        } catch (error) {
            console.error('Failed to parse RRule for event', event, error);
            expanded.push(event); // Em caso de erro, ao menos renderiza o evento original
        }
    });

    return expanded;
}

export function getAvailableSlots(
    targetDate: Date,
    workingHours: WorkingHours,
    durationMinutes: number,
    events: CalendarEvent[]
): Date[] {
    const dayOfWeek = format(targetDate, 'EEEE').toLowerCase() as DayOfWeek;
    const hours = workingHours[dayOfWeek];

    // Se o profissional não atende neste dia, retorna array vazio
    if (!hours) return [];

    const availableSlots: Date[] = [];
    const dateStr = format(targetDate, 'yyyy-MM-dd');

    // Transforma a hora inicial (ex. "08:00") e final ("18:00") do JSON em Date objects locais do targetDate
    const workStart = parse(`${dateStr} ${hours.start}`, 'yyyy-MM-dd HH:mm', new Date());
    const workEnd = parse(`${dateStr} ${hours.end}`, 'yyyy-MM-dd HH:mm', new Date());

    let currentSlotStart = workStart;
    const now = new Date();

    // Filtra os eventos apenas para o dia alvo
    const dayEvents = events.filter(e => isSameDay(new Date(e.start_time), targetDate));

    while (isBefore(currentSlotStart, workEnd)) {
        const currentSlotEnd = addMinutes(currentSlotStart, durationMinutes);

        // Não sugere slots que ultrapassem o horário de saída
        if (isAfter(currentSlotEnd, workEnd)) break;

        // Não sugere slots do passado
        if (isBefore(currentSlotStart, now) && isSameDay(currentSlotStart, now)) {
            currentSlotStart = addMinutes(currentSlotStart, durationMinutes);
            continue;
        }

        // Verifica se o slot conflita com algum evento ou bloqueio
        const hasConflict = dayEvents.some(event => {
            const eventStart = new Date(event.start_time);
            const eventEnd = new Date(event.end_time);

            // True se currentSlot cruzar a fronteira de eventStart ou eventEnd
            return (
                (isEqual(currentSlotStart, eventStart) || (isAfter(currentSlotStart, eventStart) && isBefore(currentSlotStart, eventEnd))) ||
                (isAfter(currentSlotEnd, eventStart) && isBefore(currentSlotEnd, eventEnd)) ||
                (isBefore(currentSlotStart, eventStart) && isAfter(currentSlotEnd, eventEnd))
            );
        });

        if (!hasConflict) {
            availableSlots.push(currentSlotStart);
        }

        // Avança para o próximo possível slot
        currentSlotStart = addMinutes(currentSlotStart, durationMinutes);
    }

    return availableSlots;
}
